import styled, { keyframes } from 'styled-components';
import { baseVariables, toRem } from '@marriott/mi-ui-library';

/* ── Search pill strip
   DESKTOP map view:   position: absolute, overlaid on top of the map.
   DESKTOP list view:  in-flow row, 12px vertical padding.
   MOBILE  list view:  flex-column content zone — #f4f4f4 bg, 16px padding,
                       16px gap between pill and the filter bar below it.
                       No white bg, no bottom border (design: "Content Zone").
   MOBILE  map view:   position: absolute over the map, transparent bg — only
                       the pill itself carries a white background.
────────────────────────────────────────── */
export const SearchBarStrip = styled.div<{ $isMapView?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${toRem(8)};
  padding: ${toRem(12)} ${toRem(16)};
  background: var(--t-brand-bg-color, #ffffff);
  border-bottom: ${toRem(1)} solid var(--colors-neutral-20, #eeeeee);
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  overflow: hidden;

  /* ── Mobile (<992px) ── */
  @media (max-width: 991px) {
    position: absolute;
    /* In map view, offset by chat header height (48px) to avoid overlap */
    top: ${({ $isMapView }) => ($isMapView ? toRem(48) : '0')};
    left: 0;
    right: 0;
    background: ${({ $isMapView }) => ($isMapView ? 'transparent' : 'var(--colors-neutral-10, #f4f4f4)')};
    border-bottom: none;
    padding: ${toRem(12)} ${toRem(16)};
    overflow: visible;
    pointer-events: none;
    > * {
      pointer-events: auto;
    }
  }

  /* ── Desktop / large tablet (≥992px) ── */
  @media ${baseVariables.mediaQuery.lg} {
    position: absolute;
    top: ${toRem(16)};
    left: ${toRem(16)};
    background: transparent;
    border-bottom: none;
    padding: 0;
    gap: ${toRem(16)};
    overflow: visible;
    /* Cap width so the pill never overlaps the filter button (~120px wide). */
    max-width: calc(100% - ${toRem(180)});
    pointer-events: none;
    > * {
      pointer-events: auto;
    }
  }
`;

/* Pill wrapper:
   Mobile — fills the full strip width (strip is already full-width flex-col).
   Desktop — stepwise max-width caps so the pill never pushes into the filter
             button area that lives to its right.

   Max-width formula:
     viewport - sideNavOpen(250px) - stripPadding(32px) - filterButton(~136px)
   Side nav: open=250px, closed=81px
   Filter button (chat "Filters"): ~120px + 16px margin = ~136px             */
export const PillWrapper = styled.div`
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;

  /* Mobile: full width, no cap needed (strip is already flex-col) */
  @media (max-width: 991px) {
    width: 100%;
  }

  /* Desktop: stepwise caps */
  @media (min-width: 992px) and (max-width: 1023px) {
    max-width: ${toRem(540)};
  }

  @media (min-width: 1024px) and (max-width: 1199px) {
    max-width: ${toRem(580)};
  }

  @media (min-width: 1200px) and (max-width: 1399px) {
    max-width: ${toRem(660)};
  }

  @media (min-width: 1400px) {
    max-width: ${toRem(760)};
  }
`;

/* ── Floating loading pill centered on map/panel ── */
const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.25; }
  40%            { transform: scale(1);   opacity: 1;    }
`;

export const FloatingLoadingPill = styled.div`
  position: absolute;
  top: ${toRem(80)};
  left: 50%;
  transform: translateX(-50%);
  z-index: 200;
  background: var(--t-brand-bg-color, #ffffff);
  border-radius: ${toRem(48)};
  box-shadow: 0 ${toRem(10.5)} ${toRem(30)} 0 rgba(0, 0, 0, 0.25);
  padding: ${toRem(12)} ${toRem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${toRem(6)};
  pointer-events: none;

  @media ${baseVariables.mediaQuery.lg} {
    top: ${toRem(88)};
  }
`;

export const Dot = styled.span<{ $delay: number }>`
  display: inline-block;
  width: ${toRem(8)};
  height: ${toRem(8)};
  border-radius: 50%;
  background: var(--colors-neutral-50, #303030);
  animation: ${dotBounce} 1.2s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay}ms;
`;

/* ── Wrapper: position:relative anchor for all floating children ── */
export const ResultsRelativeWrapper = styled.div<{ $isMapView?: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: visible;
`;

/* ── Mobile floating "List" toggle — reserved for future mobile list toggle feature ── */
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for future mobile list toggle feature
export const FloatingListButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: ${toRem(8)};
    position: absolute;
    bottom: ${toRem(32)};
    left: 50%;
    transform: translateX(-50%);
    z-index: 2;
    background: var(--colors-base-10, #1c1c1c);
    color: var(--colors-base-20, #ffffff);
    border: none;
    border-radius: ${toRem(9999)};
    padding: ${toRem(12)} ${toRem(24)};
    font-size: ${toRem(14)};
    font-weight: 500;
    line-height: 1;
    box-shadow: 0 ${toRem(4)} ${toRem(14)} 0 rgba(0, 0, 0, 0.4);
    cursor: pointer;
    white-space: nowrap;

    .icon-list-view {
      font-size: ${toRem(16)};
    }
  }
`;
